import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useLocation, useParams, matchPath } from "react-router-dom";

import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import RatingsContentSwitch from "./diff/RatingsContentSwitch";
import SleRatingTable from "../display/SleRatingTable";
import useAutoSave from "../../hooks/useAutoSave";
import useCommentSave from "../../hooks/ratings/useCommentSave";
import useCommentState from "../../hooks/ratings/useCommentState";
import useFlushOnLeave from "../../hooks/ratings/useFlushOnLeave";
import useHomeroomSleRatings from "../../hooks/useHomeroomSleRatings";
import useRatingSave from "../../hooks/ratings/useRatingSave";
import useRatingState from "../../hooks/ratings/useRatingState";
import useSubjectSelection from "../../hooks/useSubjectSelection";

import { buildContentItems } from "../../utils/teacher/StudentPageUtils";
import { extractStudentGradingProfileId, getGradingCriteriaId } from "../../utils/teacher/InputRatingUtils";
import { removeHiddenChars } from "../../utils/removeHiddenChars";
import { resolveSchoolLevelTemplateOverrideForSle } from "../../utils/teacher/SleBatchUtils";
import { GlobalContext } from "../contextProvider/ContextProvider";
import { useEnsureSchoolTemplatesForLocation } from "../contextProvider/ContextSetter";

import "../../styles/BatchStudentRatings.scss";

/**
 * HomeroomRatings — batch rating view for HOMEROOM mode.
 *
 * Responsibilities:
 * - EPR (grade) selection when teacher has multiple homeroom profiles
 * - Subject / SLE selection
 * - Fetching existing ratings and student subject comments
 * - Rendering RatingGrid (subjects) and SleRatingTable (SLE)
 * - Save path via useRatingSave / useCommentSave
 *
 * Explicitly does NOT handle:
 * - Group selection or shared group logic (see GroupRatings)
 * - School / grade dropdowns for groups
 * - sharedGradingProfileDtos or allSharedGroupOptions
 *
 * @param {Object}   props.state     - local state from BatchStudentRatings
 * @param {Function} props.dispatch
 * @param {string}   [props.basePath]
 */
const HomeroomRatings = ({ dispatch, state, basePath }) => {
    const { state: locationState, pathname } = useLocation();
    const { gradingProfileId: paramGradingProfileId, locationKey: urlLocationKey } = useParams();

    const gradingProfileId = useMemo(() => {
        if (paramGradingProfileId) return paramGradingProfileId;
        const match = matchPath(`${basePath || ""}/summary/input-ratings/homeroom/:id/*`, pathname);
        const matchedId = match?.params?.id;
        return matchedId && !Number.isNaN(Number(matchedId)) ? matchedId : null;
    }, [paramGradingProfileId, pathname, basePath]);

    const ensureTemplatesForLocation = useEnsureSchoolTemplatesForLocation();

    const { state: globalState } = useContext(GlobalContext);
    const {
        actionWindowsDto,
        currentTimePeriodDto,
        schoolLevelTemplateDto,
        schoolTemplatesByLocation,
        schoolYearDto,
        token,
        userDetails,
        viewTimePeriodDto
    } = globalState || {};

    const { gradingProfiles, navCollapsed, selectedEpr } = state;

    // ── Local state ────────────────────────────────────────────────────────────
    // existingRatingsData: server shape used by buildRatingMap to resolve
    // existingKey + gradingCriteria per record at save time.
    const [existingRatingsData, setExistingRatingsData] = useState([]);
    const [sleSelected, setSleSelected] = useState(Boolean(locationState?.sleSelected));

    // ── Rating + comment state ─────────────────────────────────────────────────
    const {
        ratings,
        ratingsLoaded,
        hasChanges: hasRatingChanges,
        setHasChanges: setHasRatingChanges,
        initializeFromServer: initRatings,
        setRating,
        setBulkRating,
        resetChanges: resetRatings,
        setRatingsLoaded
    } = useRatingState();

    const {
        comments,
        fluencyByKey,
        existingComments,
        hasCommentChanges,
        initializeFromServer: initComments,
        syncFromServer: syncComments,
        setComment,
        setFluencyWpm,
        resetComments,
        setHasCommentChanges
    } = useCommentState({ mode: "HOMEROOM" });

    // hasChanges drives useAutoSave — true when either ratings or comments have unsaved edits
    const hasChanges = hasRatingChanges || hasCommentChanges;

    // ── EPR / profiles ─────────────────────────────────────────────────────────
    const effectiveTimePeriodKey = (viewTimePeriodDto || currentTimePeriodDto)?.key;

    const gradingProfilesCurrentSem = useMemo(
        () => (gradingProfiles || []).filter((p) => Number(p.timePeriod) === Number(effectiveTimePeriodKey)),
        [gradingProfiles, effectiveTimePeriodKey]
    );

    // Auto-select EPR from URL param or default to first available
    useEffect(() => {
        if (gradingProfilesCurrentSem.length === 0) return;

        if (gradingProfileId && selectedEpr?.key !== gradingProfileId) {
            const profileToSet = gradingProfilesCurrentSem.find((p) => p.key === gradingProfileId);
            if (profileToSet) dispatch({ type: "SET_SELECTED_EPR", payload: profileToSet });
        }
        if (!selectedEpr && gradingProfilesCurrentSem.length > 0) {
            dispatch({ type: "SET_SELECTED_EPR", payload: gradingProfilesCurrentSem[0] });
        }
    }, [gradingProfileId, gradingProfilesCurrentSem, dispatch, selectedEpr]);

    // ── School template ────────────────────────────────────────────────────────
    // Admin teacher-view URLs include :locationKey. Use that school's template so
    // district admins see the teacher's subjects/SLE, not their own school.
    const schoolLevelTemplateOverride = useMemo(
        () =>
            resolveSchoolLevelTemplateOverrideForSle({
                view: "HOMEROOM",
                selectedSchoolLocationKey: null,
                urlLocationKey,
                schoolTemplatesByLocation
            }),
        [urlLocationKey, schoolTemplatesByLocation]
    );

    const schoolProfileLocationKey =
        schoolLevelTemplateOverride?.firstSemester?.profileLocationKey ??
        schoolLevelTemplateDto?.firstSemester?.profileLocationKey ??
        "";

    useEffect(() => {
        if (urlLocationKey) ensureTemplatesForLocation({ locationKey: urlLocationKey });
    }, [urlLocationKey, ensureTemplatesForLocation]);

    // ── Subject selection ──────────────────────────────────────────────────────
    const { subjectOptions, selectedTemplate, setSelectedTemplate } = useSubjectSelection({
        combinedGrades: null,
        selectedEpr,
        schoolLevelTemplateDto,
        schoolLevelTemplateOverride,
        incomingTemplateId: locationState?.subjectId ?? null,
        currentTimePeriodName: currentTimePeriodDto?.name,
        selectedGrade: null,
        view: "HOMEROOM"
    });

    // ── SLE ────────────────────────────────────────────────────────────────────
    const homeroomSle = useHomeroomSleRatings({
        enabled: sleSelected,
        profileLocationKey: schoolProfileLocationKey,
        selectedEpr,
        token,
        userGuid: userDetails?.uid
    });

    const homeroomSleStudents = useMemo(
        () =>
            (selectedEpr?.gradingProfileStudents || []).map((s) => ({
                ...s,
                key: s.key ?? null,
                gradingProfileStudentId: Number(s.key)
            })),
        [selectedEpr]
    );

    const homeroomSleState = useMemo(
        () => ({
            hasChanges: homeroomSle.hasChanges,
            loading: homeroomSle.loading,
            valuesByStudent: homeroomSle.valuesByStudent || {},
            sleCommentsByStudent: homeroomSle.sleCommentsByStudent || {},
            onChangeSleRating: homeroomSle.onChangeSleRating,
            onChangeSleComment: homeroomSle.onChangeSleComment,
            saveSleRatings: homeroomSle.saveHomeroomSleRatings,
            schoolLevelExpectations: homeroomSle.schoolLevelExpectations || []
        }),
        [homeroomSle]
    );

    // ── Fetch ratings + comments ───────────────────────────────────────────────
    const fetchRatings = useCallback(() => {
        if (!selectedEpr || !selectedTemplate || !token) return;

        const gradingCriteria = selectedEpr.gradingCriteriaList?.find(
            (c) => Number.parseInt(c.parentTemplate, 10) === selectedTemplate.templateId
        );
        if (!gradingCriteria) return;

        ElementaryProgressReportDao({
            action: "getContentRatings",
            gradingProfileId: selectedEpr.key,
            gradingCriteriaId: gradingCriteria.key,
            token
        })
            .then((response) => {
                if (response?.data?.payload) {
                    setExistingRatingsData(response.data.payload);
                    initRatings(response.data.payload);
                }
            })
            .catch((error) => {
                toast.error("There was a problem loading existing ratings.");
                console.error(error);
            });

        ElementaryProgressReportDao({
            action: "getStudentCriteriaComments",
            gradingCriteriaId: gradingCriteria.key,
            token
        })
            .then((response) => {
                if (response?.data?.payload) {
                    initComments(response.data.payload);
                }
            })
            .catch((error) => {
                toast.error("There was a problem loading existing comments.");
                console.error(error);
            });
    }, [selectedEpr, selectedTemplate, token, initRatings, initComments]);

    useEffect(() => {
        if (!sleSelected && selectedEpr && selectedTemplate) {
            fetchRatings();
        }
    }, [fetchRatings, selectedEpr, selectedTemplate, sleSelected]);

    // ── Save hooks ─────────────────────────────────────────────────────────────
    const contentItemsForSave = useMemo(
        () => (selectedTemplate ? buildContentItems(selectedTemplate) : []),
        [selectedTemplate]
    );

    const { saveRatings } = useRatingSave({
        mode: "HOMEROOM",
        ratings,
        contentItems: contentItemsForSave,
        existingRatingsData,
        groupContext: null,
        selectedEpr,
        selectedTemplate,
        userDetails,
        token,
        setHasChanges: setHasRatingChanges,
        setRatingsLoaded,
        setExistingRatingsData,
        fetchSelectedGroupInfo: undefined
    });

    const { saveComments } = useCommentSave({
        mode: "HOMEROOM",
        comments,
        fluencyByKey,
        existingComments,
        groupContext: null,
        userDetails,
        token,
        setHasCommentChanges,
        syncFromServer: syncComments
    });

    // ── Auto-save ──────────────────────────────────────────────────────────────
    useAutoSave(saveRatings, hasRatingChanges);
    useAutoSave(saveComments, hasCommentChanges);

    // ── Rating / comment handlers (passed to RatingGrid) ──────────────────────
    const handleRatingChange = useCallback(
        (studentKey, templateContentId, rating) => {
            setRating(studentKey, templateContentId, rating);
        },
        [setRating]
    );

    const handleBulkRating = useCallback(
        (templateContentId, rating) => {
            const editableKeys = (selectedEpr?.gradingProfileStudents || []).map((s) => String(s.key));
            setBulkRating(templateContentId, rating, editableKeys);
        },
        [setBulkRating, selectedEpr]
    );

    const handleCommentChange = useCallback(
        (studentKey, comment, gradingCriteriaId) => {
            const key = `${studentKey}_${gradingCriteriaId}`;
            // useCommentState.setComment expects the full composite key
            setComment(key, comment);
        },
        [setComment]
    );

    const handleFluencyChange = useCallback(
        (studentKey, wpm, gradingCriteriaId) => {
            setFluencyWpm(`${studentKey}_${gradingCriteriaId}`, wpm);
        },
        [setFluencyWpm]
    );

    // Per-cell comment save — calls updateContentRating (singular) then refreshes
    const handleSaveComment = useCallback(
        (context) => {
            if (!context) return;
            const { student, contentItem, rating, comment, key: existingKey } = context;
            const gradingCriteria = getGradingCriteriaId(student, [], selectedEpr, selectedTemplate, "HOMEROOM");
            const payload = {
                key: existingKey,
                gradingProfileStudentId: extractStudentGradingProfileId(student.key),
                gradingCriteria: Number(gradingCriteria),
                templateContentId: Number(contentItem.templateContentId),
                contentType: contentItem.contentType,
                rating: rating === "DOES NOT MEET" ? "DOES_NOT_MEET" : rating,
                comment: removeHiddenChars(comment.trim()),
                personalizedLearningPlan: false,
                lastRatedGuid: userDetails?.uid
            };
            ElementaryProgressReportDao({ action: "updateContentRating", ratingKey: existingKey, data: payload, token })
                .then((response) => {
                    if (response?.data?.payload) {
                        toast.success("Comment saved.");
                        fetchRatings();
                    }
                })
                .catch(() =>
                    toast.error("Comment was not saved. Please make note of your comment and try again later.")
                );
        },
        [selectedEpr, selectedTemplate, userDetails, token, fetchRatings]
    );

    // ── Handlers ───────────────────────────────────────────────────────────────
    // Flush pending work BEFORE resetting on subject / EPR change. Await so the
    // update request finishes while this subject's state is still current.
    const commitIfNeeded = useCallback(async () => {
        const pending = [];
        if (hasRatingChanges) pending.push(saveRatings());
        if (hasCommentChanges) pending.push(saveComments());
        if (sleSelected && homeroomSle?.hasChanges) pending.push(homeroomSle.saveHomeroomSleRatings());
        if (pending.length) await Promise.all(pending);
    }, [hasRatingChanges, hasCommentChanges, saveRatings, saveComments, homeroomSle, sleSelected]);

    // Unmount-only leave flush (side nav / breadcrumb). Stable leaveKey so subject/EPR
    // switches stay on awaited commitIfNeeded above — do not double-flush via leaveKey.
    const leaveDirty = hasRatingChanges || hasCommentChanges || (sleSelected && !!homeroomSle?.hasChanges);
    useFlushOnLeave(commitIfNeeded, leaveDirty, "homeroom-ratings");

    const resetSubjectCaches = useCallback(() => {
        resetRatings();
        resetComments();
        setExistingRatingsData([]);
    }, [resetRatings, resetComments]);

    const handleHomeroomEprChange = useCallback(
        async (profile) => {
            await commitIfNeeded();
            dispatch({ type: "SET_SELECTED_EPR", payload: profile });
            setSelectedTemplate(null);
            setSleSelected(false);
            resetSubjectCaches();
        },
        [commitIfNeeded, dispatch, setSelectedTemplate, resetSubjectCaches]
    );

    const handleSubjectChange = useCallback(
        async (e) => {
            const value = e.target.value;
            if (!value) return;

            await commitIfNeeded();
            resetSubjectCaches();

            if (value === "SLE") {
                setSleSelected(true);
                setSelectedTemplate(null);
                return;
            }

            setSleSelected(false);
            const templateId = Number(value);
            const subject = subjectOptions.find((s) => s.templateId === templateId) || null;
            setSelectedTemplate(subject);
        },
        [commitIfNeeded, resetSubjectCaches, setSelectedTemplate, subjectOptions]
    );

    // ── Render conditions ──────────────────────────────────────────────────────
    const shouldShowEprSelect = !gradingProfileId && gradingProfilesCurrentSem?.length > 1;
    const shouldShowRatingGrid =
        selectedEpr && selectedTemplate && (selectedEpr.gradingProfileStudents?.length ?? 0) > 0;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="right-side-wrapper" style={navCollapsed ? { maxWidth: "95%" } : { maxWidth: "80%" }}>
            <div className="right-page-title">
                <h4>
                    Input Ratings{" "}
                    {selectedEpr && (
                        <span>
                            &ndash;{" "}
                            {selectedEpr.gradeName === "K"
                                ? "Kindergarten"
                                : `${selectedEpr.gradeName} Grade Homeroom Report Card`}
                        </span>
                    )}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto?.name}
                </h5>
            </div>
            <hr />

            {!gradingProfilesCurrentSem?.length && (
                <div>
                    <b>Homeroom Teachers:</b> you must first create your homeroom Report Card before you can input
                    ratings.
                </div>
            )}

            <div style={{ display: "flex", gap: "0px 24px", flexWrap: "wrap" }}>
                {shouldShowEprSelect && (
                    <section className="epr-selector-ratings">
                        <h5>Select a Homeroom Report Card:</h5>
                        <label className="visually-hidden" htmlFor="homeroomEprSelect">
                            Select a Homeroom Report Card
                        </label>
                        <select
                            className="rating-select-dropdown"
                            id="homeroomEprSelect"
                            value={selectedEpr?.key || ""}
                            onChange={(e) => {
                                const selected = gradingProfilesCurrentSem.find(
                                    (p) => p.key.toString() === e.target.value.toString()
                                );
                                handleHomeroomEprChange(selected);
                            }}
                        >
                            <option value="" disabled>
                                Select Grade
                            </option>
                            {[...gradingProfilesCurrentSem]
                                .sort((a, b) => a.grade - b.grade)
                                .map((profile) => (
                                    <option key={profile.key} value={profile.key}>
                                        {profile.gradeName === "K" ? "Kindergarten" : `${profile.gradeName} Grade`}
                                    </option>
                                ))}
                        </select>
                    </section>
                )}

                {selectedEpr && (
                    <div className="subject-dropdown-wrapper">
                        <section className="epr-selector-ratings">
                            <h5>Select a Subject:</h5>
                            <label className="visually-hidden" htmlFor="homeroomSubjectDropdown">
                                Select a Subject
                            </label>
                            <select
                                id="homeroomSubjectDropdown"
                                className="rating-select-dropdown"
                                onChange={handleSubjectChange}
                                value={(() => {
                                    if (selectedTemplate) return String(selectedTemplate.templateId);
                                    return sleSelected ? "SLE" : "";
                                })()}
                            >
                                <option value="" disabled>
                                    Select a Subject
                                </option>
                                <option value="SLE">SLE</option>
                                {subjectOptions.map((s) => (
                                    <option key={s.templateId} value={s.templateId}>
                                        {s.subjectName}
                                    </option>
                                ))}
                            </select>
                        </section>
                    </div>
                )}
            </div>

            {selectedEpr && (selectedEpr.gradingProfileStudents?.length ?? 0) === 0 && (
                <div>
                    No students are rostered in this Report Card. Please go to the Report Card Roster section to add
                    your homeroom students before entering grades or comments.
                </div>
            )}

            {shouldShowRatingGrid && (
                <RatingsContentSwitch
                    key={selectedTemplate.templateId}
                    diffProps={{
                        actionWindowsDto,
                        basePath,
                        existingRatingsData,
                        mode: "HOMEROOM",
                        onSaveSubjectComments: saveComments,
                        onSubjectCommentChange: handleCommentChange,
                        selectedEpr,
                        selectedTemplate,
                        subjectComments: comments,
                        token,
                        userDetails
                    }}
                    ratingGridProps={{
                        actionWindowsDto,
                        basePath,
                        comments,
                        fluencyByKey,
                        existingRatingsData,
                        hasChanges,
                        hasCommentChanges,
                        mode: "HOMEROOM",
                        onBulkRating: handleBulkRating,
                        onCommentChange: handleCommentChange,
                        onFluencyChange: handleFluencyChange,
                        onRatingChange: handleRatingChange,
                        onSave: saveRatings,
                        onSaveComments: saveComments,
                        onSaveComment: handleSaveComment,
                        ratings,
                        ratingsLoaded,
                        selectedEpr,
                        fluencyGradeName: selectedEpr?.gradeName,
                        selectedTemplate,
                        token,
                        userDetails
                    }}
                />
            )}

            {sleSelected && selectedEpr && (
                <SleRatingTable
                    actionWindowsDto={actionWindowsDto}
                    mode="HOMEROOM"
                    selectedEpr={selectedEpr}
                    sleState={homeroomSleState}
                    students={homeroomSleStudents}
                    token={token}
                    schoolYearKey={selectedEpr?.schoolYearKey ?? schoolYearDto?.key}
                    basePath={basePath}
                    userDetails={userDetails}
                />
            )}
        </div>
    );
};

HomeroomRatings.propTypes = {
    basePath: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    state: PropTypes.shape({
        gradingProfiles: PropTypes.array,
        navCollapsed: PropTypes.bool,
        selectedEpr: PropTypes.object
    }).isRequired
};

export default HomeroomRatings;
